import { KatAlert, KatIcon, KatInput, KatInputGroup } from "@amzn/katal-react";
import classnames from "classnames";
import React, { type FC } from "react";
import { useTranslation } from "react-i18next";
import styles from "src/styles.module.scss";
import {
  DEFAULT_TRANSLATION_CONFIG,
  DEFAULT_TRANSLATION_NS,
} from "../TranslatedField/TranslationKey";

export type SearchBarProps = {
  searchablePattern: string;
  title: string;
  placeholder?: string;
  showClearButton: boolean;
  onChange: (searchPattern: string) => void;
  onKeyupInput?: (letter: KatInput.KeyupEvent) => void;
  onClear: () => void;
  classes?: Array<string>;
  shouldShowWarning?: boolean;
  warningMsg?: string;
  disableSearch?: boolean;
};

const SearchBar: FC<SearchBarProps> = ({
  searchablePattern,
  title,
  placeholder,
  showClearButton,
  onChange,
  onKeyupInput = () => {},
  onClear,
  classes,
  shouldShowWarning,
  warningMsg,
  disableSearch,
}: SearchBarProps) => {
  const { t } = useTranslation(
    DEFAULT_TRANSLATION_NS,
    DEFAULT_TRANSLATION_CONFIG
  );

  const onChangeInput = (e: KatInput.ChangeEvent) => {
    const { value } = e.target as HTMLInputElement;
    onChange(value);
  };

  return (
    <div
      className={classnames(
        styles.flex,
        styles.justify_end,
        styles.w_50,
        styles.align_items_stretch,
        classes
      )}
    >
      <div
        className={classnames(
          styles.flex,
          styles.flex_direction_row,
          styles.flex_nowrap,
          styles.align_items_start,
          styles.justify_end,
          styles.w_100
        )}
      >
        <div className={classnames(styles.mb_zero_important, styles.ma_6)}>
          <h5>{title}</h5>
        </div>
        <div
          className={classnames(
            styles.flex,
            styles.flex_direction_column,
            styles.align_items_start,
            styles.w_100
          )}
        >
          <div className={classnames(styles.w_100, styles.mb_8)}>
            <KatInputGroup>
              <KatInput
                id="document-search-input"
                placeholder={placeholder}
                value={searchablePattern}
                onChange={onChangeInput}
                onKeyup={onKeyupInput}
              />
              {showClearButton ? (
                <>
                  <button
                    id="clear-document-search-button"
                    onClick={onClear}
                    aria-label={t("aic_search_clear_button")}
                    type="button"
                    className={classnames(styles.button_hover_point)}
                  >
                    <KatIcon
                      id="clear-document-search-icon"
                      name="x-circle-fill"
                      size="tiny"
                    />
                  </button>
                </>
              ) : (
                <></>
              )}
              <button
                id="document-search-button"
                aria-label={t("aic_search_submit_button")}
                type="button"
                disabled={disableSearch}
                className={classnames({
                  [styles.button_hover_point]: !disableSearch,
                })}
              >
                <KatIcon
                  className={classnames({
                    [styles.kat_color_color_grey]: disableSearch,
                  })}
                  id="document-search-icon"
                  name="search"
                  size="small"
                />
              </button>
            </KatInputGroup>
          </div>
          <KatAlert
            description={warningMsg}
            header="Informational Alert"
            variant="warning"
            dismissed={!shouldShowWarning}
          ></KatAlert>
        </div>
      </div>
    </div>
  );
};

export default SearchBar;
